import {
  VStack,
  Flex,
  Icon,
  Button,
  useDisclosure,
  RemoveAlertDialog,
  FormControl,
  FormErrorMessage,
} from '@wegroup/design-system';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEditState from '../../../../../../../common/src/hooks/useEditState';
import { useFlowsDispatch } from '../../../../../../../common/src/utils/globalVariables';
import { removeAnswerForIndex } from '../../../../../store/redux/slices/flowsSlice';
import { FlowsQuestion } from '../../../../../types/FlowsTypes';
import AreYouSureModal from '../../../../../../../common/src/components/AreYouSureModal';
import useRemoveTableItemLegacy from '../hooks/useRemoveTableItemLegacy';
import NewTableItemModal from './NewTableItemModal';
import ItemsTable from './ItemsTable';
import useDuplicateGroupIds from '../hooks/useDuplicateGroupIds';
import { handleResponseLanguage } from '../../../../../../../../i18n';
import { capitalize } from 'lodash';
import { getFlowsDataTestId } from '../../../../../utils/testUtils';
import { Inputs } from '../../../../../types/Enums';
import useRemoveTableItem from '../hooks/useRemoveTableItem';
import useEntityReference from '../hooks/useEntityReference';
import useFlowInputComponent from '../../../../../hooks/useFlowsInputComponent';

interface Props {
  question: FlowsQuestion;
}

const DEFAULT_INDEX = -1;

const TableInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const flowsDispatch = useFlowsDispatch();
  const { error } = useFlowInputComponent({ question });

  const { deleteEntity } = useRemoveTableItem(question);
  const removeTableItem = useRemoveTableItemLegacy(question);
  const entity_name = handleResponseLanguage(
    question.rules.table?.entity_name,
  ).toLowerCase();
  const duplicateGroupIds = useDuplicateGroupIds(question.id);

  // CLOSE ALERT
  const {
    isOpen: isCloseAlertOpen,
    onOpen: onOpenCloseAlert,
    onClose: onCloseCloseAlert,
  } = useDisclosure();

  const handleOnClose = () => {
    setCurrentIndex(DEFAULT_INDEX);
    onClose();
  };

  const handleOnYes = () => {
    onCloseCloseAlert();
    handleOnClose();
  };

  // EDIT
  const { editIndex: currentIndex, setEditIndex: setCurrentIndex } =
    useEditState(DEFAULT_INDEX);

  const handleOnEdit = (index: number) => {
    setCurrentIndex(index);
    onOpen();
  };

  // REMOVE
  const {
    isOpen: isAlertOpen,
    onOpen: onAlertOpen,
    onClose: onAlertClose,
  } = useDisclosure();
  const [toBeRemovedIndex, setToBeRemoveIndex] = useState<number>();
  const { getEntity, isFetching } = useEntityReference(
    question.id,
    toBeRemovedIndex,
  );

  const handleOnRemove = (index: number) => {
    setToBeRemoveIndex(index);
    onAlertOpen();
  };

  const handleOnAlertClose = () => {
    setToBeRemoveIndex(undefined);
    onAlertClose();
  };

  const handleOnOpen = () => {
    duplicateGroupIds.forEach((duplicateGroupId) => {
      flowsDispatch(
        removeAnswerForIndex({ forIndex: -1, groupId: duplicateGroupId }),
      );
    });

    onOpen();
  };

  const handleOnRemoveConfirm = async () => {
    const entity = await getEntity();
    if (entity) {
      if (question.rules.table?.delete_method === 'BY_PARTY_ID') {
        deleteEntity(entity.id, toBeRemovedIndex, entity.party_id);
      } else {
        deleteEntity(entity.id, toBeRemovedIndex);
      }
    } else {
      removeTableItem(toBeRemovedIndex);
    }
    handleOnAlertClose();
  };

  return (
    <FormControl isInvalid={!!error}>
      <VStack spacing="4" align="stretch" gridColumn="span 2" maxW="95vw">
        <AreYouSureModal
          description={capitalize(
            t(`FLOWS_INPUT_TABLE_ALL-DATA-WILL-BE-LOST`, {
              entity: entity_name,
            }),
          )}
          onClose={onCloseCloseAlert}
          onConfirm={handleOnYes}
          isOpen={isCloseAlertOpen}
        />
        <RemoveAlertDialog
          title={capitalize(
            t(`FLOWS_INPUT_TABLE_REMOVE`, {
              entity: entity_name,
            }),
          )}
          description={capitalize(
            t(`FLOWS_INPUT_TABLE_ARE-YOU-SURE`, {
              entity: entity_name,
            }),
          )}
          isOpen={isAlertOpen}
          onClose={handleOnAlertClose}
          onConfirm={handleOnRemoveConfirm}
          isLoading={isFetching}
        />
        {isOpen && (
          <NewTableItemModal
            question={question}
            isOpen={isOpen}
            onCancel={onOpenCloseAlert}
            onClose={handleOnClose}
            currentIndex={currentIndex}
          />
        )}

        <ItemsTable
          question={question}
          onEdit={handleOnEdit}
          onRemove={handleOnRemove}
          isDisabled={question.rules.is_disabled}
          isInvalid={!!error}
        />
        <Flex justify="flex-end">
          <Button
            onClick={handleOnOpen}
            leftIcon={<Icon name="GeneralActionsAdd" />}
            data-test-id={getFlowsDataTestId(
              Inputs.TABLE,
              question,
              'ADD-BUTTON',
            )}
            isDisabled={question.rules.is_disabled}
          >
            {capitalize(t(`FLOWS_INPUT_TABLE_ADD`, { entity: entity_name }))}
          </Button>
        </Flex>
      </VStack>
      {error?.message && (
        <FormErrorMessage justifyContent="flex-end">
          {error.message}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export default TableInputComponent;
